import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { AngularFirestore } from '@angular/fire/firestore';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

import {
  COMPUTASSIST_ORDER_SUFFIX,
  ComputAssistPharmacy,
  ComputAssistScriptRequest,
  ComputAssistOrderStatusRecord,
  ComputAssistSubmitResponse,
} from './models/computassist.model';
import { MedicationPrescription, MedicationPrescriptionLine, ClinicalNewClient, SendComputAssistScript } from './api-client.service';
import { dropNullUndefined } from '@app/core/functions/drop-null-undefined';
import { buildEscriptOrderNumber } from '@app/core/functions/escript-order-number';

// Pack-level VolumeUnits values (Medprax) marking measured packs: 'ml' liquids, 'g' creams/gels; empty = countable
const MEASURED_PACK_UNITS = ['ml', 'g'];

@Injectable({
  providedIn: 'root',
})
export class ComputAssistApiService {
  constructor(
    private firestore: AngularFirestore,
    private httpClient: HttpClient,
    private clinicalNewClient: ClinicalNewClient
  ) {}

  /** CA requires 9-10 significant NAPPI characters (product + pack); a shorter code can never match. */
  findIncompletePackCodeLine(prescription: MedicationPrescription): MedicationPrescriptionLine | undefined {
    return (prescription.MedicationPrescriptionLines ?? []).find(line => this.significantNappiLength(line) < 9);
  }

  /** Leading zeros are padding: '000123456' and '123456' are the same 6-digit product code. */
  private significantNappiLength(line: MedicationPrescriptionLine): number {
    return String(line.NappiCode10 || line.NappiCode || '').replace(/^0+/, '').length;
  }

  /**
   * Sends a prescription to the ComputAssist SmartSwitch eScripting platform.
   *
   * Flow:
   * 1. Build the ComputAssist JSON payload from the prescription
   * 2. Call Florence proxy endpoint (static IP required)
   * 3. Write initial pending status to Firestore (both paths)
   */
  async sendScriptToComputAssist(
    tenantId: string,
    prescription: MedicationPrescription,
    pharmacy: ComputAssistPharmacy,
    pdfUrl: string,
    encounterId: string
  ): Promise<void> {
    const invalidLine = this.findIncompletePackCodeLine(prescription);
    if (invalidLine) {
      throw new Error(
        `Cannot send to ComputAssist: "${invalidLine.MedicationDescription ?? 'a medicine'}" has an incomplete NAPPI pack code. Remove and re-add the medicine, then retry.`
      );
    }

    const pdfBlob = await this.httpClient.get(pdfUrl, { responseType: 'blob' }).toPromise();
    const pdfBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });

    const scriptNumber = this.getScriptNumber(prescription);
    const payload = this.buildScriptPayload(prescription, pharmacy, pdfBase64, scriptNumber);

    const request: SendComputAssistScript = {
      ScriptRequest: JSON.stringify(payload),
    };

    const response = await this.clinicalNewClient.sendComputAssistScript(tenantId, encounterId, request).toPromise();

    const scriptResponse = JSON.parse(response.Data.ScriptResponse) as ComputAssistSubmitResponse;

    if (scriptResponse.code !== 0) {
      throw new Error(`ComputAssist rejected the script: ${scriptResponse.message}`);
    }

    // Write initial status + audit record to Firestore
    const sentAt = new Date().toISOString();
    const initialStatus: ComputAssistOrderStatusRecord = {
      statusCode: -1, // -1 = pending (awaiting webhook response from pharmacy)
      statusUpdatedAt: sentAt,
      statusHistory: [],
    };

    const practiceRef = this.getFirestoreDocForPrescription(prescription, scriptNumber);
    await practiceRef.set(
      {
        scriptNumber,
        encounterId,
        prescriptionId: prescription.MedicationPrescriptionId,
        practiceId: prescription.Patient?.PracticeId,
        patientId: prescription.Patient?.PatientId,
        ...initialStatus,
        createdAt: sentAt,
        submitResponse: dropNullUndefined(scriptResponse),
      },
      { merge: true }
    );

    // Top-level doc so the webhook can resolve the practiceId path. Deliberately NO status fields
    // here: if the webhook lands before this write, a merged statusCode:-1/statusHistory:[] would
    // clobber the real status. A missing statusCode renders as "Sent to pharmacy" anyway.
    this.firestore
      .collection('ComputAssistOrders')
      .doc(scriptNumber)
      .set(
        {
          scriptNumber,
          practiceId: prescription.Patient?.PracticeId,
          patientId: prescription.Patient?.PatientId,
          encounterId,
          prescriptionId: prescription.MedicationPrescriptionId,
          clinicalPracticeRequestPath: `ClinicalPractice/${prescription.Patient?.PracticeId}/ComputAssistRequests/${scriptNumber}`,
          createdAt: sentAt,
        },
        { merge: true }
      );
  }

  /** Generates the script number for a prescription (shared HBC-...-CA format). */
  getScriptNumber(prescription: MedicationPrescription): string {
    return buildEscriptOrderNumber(prescription, COMPUTASSIST_ORDER_SUFFIX);
  }

  private buildScriptPayload(
    prescription: MedicationPrescription,
    pharmacy: ComputAssistPharmacy,
    pdfBase64: string,
    scriptNumber: string
  ): ComputAssistScriptRequest {
    const p = prescription;
    const patient = p.Patient;
    const accountDetails = patient?.PatientAccountDetails;
    const mainMember = accountDetails?.MedicalAidMainMemberDetails;
    const patientDetails = patient?.PatientDetails;
    const provider = p.Provider;

    const isTestMode = !environment.production;

    // ComputAssist pre-defaults only keys that are present and empty (''); null/absent keys break
    // their mapping - emit the full key set. mixingredients stays absent (null/ignored for non-mix).
    return {
      vendorid: 'hbridge',
      pharmacy: { ramsno: pharmacy.ramsno },
      doctor: {
        name: provider?.TreatingDoctorName ?? '',
        practicename: provider?.PracticeName ?? '',
        practiceno: provider?.TreatingDoctorPracticeNumber ?? '',
        address1: '',
        address2: '',
        address3: '',
        postal: '',
        phoneno: provider?.CellphoneNumber ?? '',
        email: provider?.EmailAddress ?? '',
      },
      member: {
        // CA rejects scripts without an ID/passport (Louis, 17 Aug) - passport fills in when no SA ID
        mainidno: mainMember?.IdentityNo || mainMember?.PassportNo || '',
        name: mainMember?.FirstName ?? '',
        surname: mainMember?.Surname ?? '',
        title: mainMember?.Title ?? '',
        maindob: mainMember?.DateOfBirth ? moment(mainMember.DateOfBirth).format('YYYYMMDD') : '',
        gender: (mainMember?.Gender ?? '').substring(0, 1).toUpperCase(),
        language: '',
        address1: 'No address', // CA requires a non-empty profile address to allow scripting
        address2: '',
        address3: '',
        postal: '',
        email: mainMember?.EmailAddress ?? '',
        phoneno: mainMember?.ContactNo ?? '',
        cellno: mainMember?.ContactNo ?? '',
        medaid1: accountDetails?.MedicalAidName ?? '',
        medaidno: accountDetails?.MedicalAidMembershipNumber ?? '',
        medaid2: '',
        medaid3: '',
      },
      patient: {
        idno: patientDetails?.IdentityNo || patientDetails?.PassportNo || '',
        relation: accountDetails?.MedicalAidDependentCode === '01' ? '1' : '2',
        name: patientDetails?.FirstName ?? '',
        surname: patientDetails?.Surname ?? '',
        title: patientDetails?.Title ?? '',
        gender: (patientDetails?.Gender ?? '').substring(0, 1).toUpperCase(),
        language: '',
        dob: patientDetails?.DateOfBirth ? moment(patientDetails.DateOfBirth).format('YYYYMMDD') : '',
        depcode: accountDetails?.MedicalAidDependentCode ?? '01',
      },
      script: {
        sctno: scriptNumber,
        medaid: accountDetails?.MedicalAidName ?? '',
        payoff: '',
        scriptdate: moment().format('DD MMM YYYY HH:mm:ss'),
        test: isTestMode ? 'Y' : 'N',
        delivery: '0', // Default to Collect; UI can override
        itemcount: String(p.MedicationPrescriptionLines?.length ?? 0),
        items: (p.MedicationPrescriptionLines ?? []).map((line, index) => {
          // Measured packs (ml/g): the doctor's Quantity counts packs (1 = one bottle/tube, fractions allowed);
          // CA wants the amount in measure units, so send Quantity * PackSize (e.g. 1 x 100ml -> 100).
          const isMeasuredPack = MEASURED_PACK_UNITS.includes((line.Parameters?.['PackUnit'] ?? '').toLowerCase());
          const packSize = Number(line.Parameters?.['PackSize']) || 0;
          const qtyUnits = isMeasuredPack && packSize ? (line.Quantity ?? 0) * packSize : (line.Quantity ?? 0);
          return {
            itemno: String(index + 1),
            mix: '0',
            // CA product matching keys on unpadded NAPPI (significant digits + pack); NappiCode10 is zero-padded
            napcd: (line.NappiCode10 || '000' + line.NappiCode).replace(/^0+/, ''),
            descript: line.MedicationDescription ?? '',
            qty: String(qtyUnits) + '.000',
            original_script: '',
            repeats: String(line.Repeat ?? 0),
            supply: String(qtyUnits),
            // CA accepts multiple ICD-10 codes pipe-separated, e.g. "A09.0|A15|A08.5" (Louis, 25 Aug)
            icd10cd: (line.Diagnosis ?? [])
              .map(d => d.DiagnosisCode)
              .filter(Boolean)
              .join('|'),
            directions: line.Parameters?.['DosageDescription'] ?? '',
          };
        }),
      },
      message: '',
      pdf: pdfBase64,
    };
  }

  private getFirestoreDocForPrescription(prescription: MedicationPrescription, scriptNumber: string) {
    const practiceId = prescription.Patient?.PracticeId;
    if (!practiceId) {
      throw new Error(
        `ComputAssistApiService: cannot resolve Firestore doc path - practiceId is empty. ` +
          `prescriptionId=${prescription.MedicationPrescriptionId}`
      );
    }
    return this.firestore.collection('ClinicalPractice').doc(practiceId).collection('ComputAssistRequests').doc(scriptNumber);
  }
}
